import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mail, Sparkles, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      // Backend always returns 200 whether the email exists or not — the
      // success screen is the same either way so we don't leak which
      // addresses are registered.
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="lg:hidden mb-6 flex items-center gap-2">
        <div className="w-7 h-7 rounded-sm bg-slate-900 grid place-items-center">
          <Sparkles size={16} className="text-white" />
        </div>
        <div className="font-heading font-semibold">Brainydo</div>
      </div>

      {sent ? (
        <div className="w-full max-w-sm">
          <div className="w-12 h-12 rounded-full bg-emerald-50 grid place-items-center mb-4">
            <CheckCircle2 size={22} className="text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Check your email</h2>
          <p className="text-slate-600 mt-2 text-[14px] leading-relaxed">
            If <strong>{email.trim()}</strong> is registered, we've sent a link to reset your
            password. The link expires in 60 minutes.
          </p>
          <p className="text-slate-500 mt-4 text-[12.5px]">
            Didn't get it? Check spam, or{" "}
            <button
              type="button"
              onClick={() => setSent(false)}
              className="text-slate-900 underline underline-offset-2"
            >
              try a different email
            </button>.
          </p>
          <Link
            to="/login"
            className="mt-8 inline-flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="forgot-form">
          <div className="label-eyebrow mb-2">Password reset</div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight">Forgot your password?</h2>
          <p className="text-slate-600 mt-2 text-[14px]">
            Enter the email you use to sign in. We'll send you a link to set a new password.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="label-eyebrow">Email</label>
              <input
                type="email" required autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-sm bg-white outline-none focus:border-slate-900 transition text-[14px]"
                data-testid="forgot-email-input"
              />
            </div>
            <button
              type="submit" disabled={busy || !email.trim()}
              className="w-full h-10 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-medium text-[14px] rounded-sm transition disabled:opacity-60"
              data-testid="forgot-submit-btn"
            >
              <Mail size={14} />
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <p className="text-[13px] text-slate-600 pt-2">
              Remembered it?{" "}
              <Link to="/login" className="text-slate-900 underline underline-offset-2">Back to sign in</Link>
            </p>
          </div>
        </form>
      )}
    </Shell>
  );
}

// Two-column marketing shell that matches Login/Register visual language,
// kept private to this file so it's clear it's not a shared layout.
function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--sw-bg)]">
      <div className="hidden lg:block relative overflow-hidden border-r border-slate-200">
        <img
          src="https://images.unsplash.com/photo-1770009971150-f50bc7d373a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGdlb21ldHJpYyUyMGxpZ2h0JTIwYXJjaGl0ZWN0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc2ODE4NTJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-white/30 to-transparent" />
        <div className="relative z-10 p-10 flex flex-col h-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-slate-900 grid place-items-center">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="font-heading text-lg font-semibold">Brainydo</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        {children}
      </div>
    </div>
  );
}
